import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * PLACEHOLDER CONTENT.
 * The three cases rendered here come from i18n/escaCopy.ts and are illustrative
 * constructions, not real client stories. Replace them with verified cases
 * before the site goes live.
 */
export function CasesSection() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const toggle = (i: number, role: string) => {
    const next = openIdx === i ? null : i;
    setOpenIdx(next);
    if (next !== null) track('case_open', {
      case_role: role
    });
  };
  return <section id="cases" className="bg-white py-20 lg:py-28" ref={ref} data-id="element-208" data-mp="3rihrc1b1v3hu">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-209" data-mp="3mimz9ufjg7v7">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.55,
        ease: EASE
      }} className="max-w-[44rem]" data-id="element-210" data-mp="3mh272bgp510i">
          <h2 className="esca-h2 text-ink text-balance" data-id="element-211" data-mp="3dk1nqj8hht3x">{c.cases.title}</h2>
          <p className="esca-lead mt-6 text-muted max-w-[52ch]" data-id="element-212" data-mp="e1s8hdc0mdxh">
            {c.cases.lead}
          </p>
        </motion.div>

        <div className="mt-6 flex items-start gap-2.5 max-w-[52rem]" data-id="element-213" data-mp="95tg7enkgbrs">
          <span className="mt-[0.4375rem] w-1.5 h-1.5 flex-shrink-0 bg-accent" aria-hidden="true" data-id="element-214" data-mp="4axlg6qy3aaz" />
          <p className="text-[0.8125rem] text-muted leading-relaxed" data-id="element-215" data-mp="wt1j5pxiv8qn">
            {c.cases.placeholderNote}
          </p>
        </div>

        <div className="mt-12 lg:mt-16 grid lg:grid-cols-3 gap-6" data-id="element-216" data-mp="s1a1k4wcgdcn">
          {c.cases.items.map((item, i) => {
          const isOpen = openIdx === i;
          return <motion.article key={item.role + item.industry} initial={{
            opacity: 0,
            y: 16
          }} animate={inView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1 + i * 0.08,
            ease: EASE
          }} className="flex flex-col border border-line bg-white transition-colors duration-200 hover:border-muted-light" data-id="element-217" data-mp="2r9t7pkipdfxx" data-mpi={i}>
                <div className="p-7 lg:p-8 flex-1 flex flex-col" data-id="element-218" data-mp="1tlj5b4f08q6a">
                  <h3 className="font-display text-[2rem] lg:text-[2.5rem] font-bold text-ink tracking-[-0.04em] leading-none" data-id="element-219" data-mp="1tnca0e6250bn">
                    {item.role}
                  </h3>

                  <dl className="mt-6 space-y-2.5 pb-6 border-b border-line" data-id="element-220" data-mp="3o0tz45m4258s">
                    <div className="flex gap-3 text-[0.8125rem]" data-id="element-221" data-mp="3syvi6qjje4k9">
                      <dt className="w-24 flex-shrink-0 text-muted" data-id="element-222" data-mp="1hb9z05xdq9eq">
                        {c.cases.industryLabel}
                      </dt>
                      <dd className="text-ink" data-id="element-223" data-mp="1cdi5j7h36f8f">{item.industry}</dd>
                    </div>
                    <div className="flex gap-3 text-[0.8125rem]" data-id="element-224" data-mp="1r24um0wimlcu">
                      <dt className="w-24 flex-shrink-0 text-muted" data-id="element-225" data-mp="1mamvlie1yuui">
                        {c.cases.geoLabel}
                      </dt>
                      <dd className="text-ink" data-id="element-226" data-mp="200gj9xdtn92n">{item.geo}</dd>
                    </div>
                  </dl>

                  <div className="mt-6" data-id="element-227" data-mp="1vzmu70on7o86">
                    <p className="esca-eyebrow text-muted" data-id="element-228" data-mp="29zxl23pyd2tc">
                      {c.cases.challengeLabel}
                    </p>
                    <p className="mt-2.5 text-[0.9375rem] text-ink leading-relaxed" data-id="element-229" data-mp="250kylk2ztxra">
                      {item.challenge}
                    </p>
                  </div>

                  <AnimatePresence initial={false} data-id="element-230" data-mp="an3a9uurkwv7">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: EASE
                }} className="overflow-hidden" data-id="element-231" data-mp="c40nqe3ujahz">
                        <div className="pt-6" data-id="element-232" data-mp="r0rddvnpx7ue">
                          <p className="esca-eyebrow text-muted" data-id="element-233" data-mp="3pj5b9a8qsd3f">
                            {c.cases.approachLabel}
                          </p>
                          <p className="mt-2.5 text-[0.9375rem] text-ink leading-relaxed" data-id="element-234" data-mp="3uihxbsrshg4y">
                            {item.approach}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="mt-auto pt-7" data-id="element-235" data-mp="36rcldooyz7zx">
                    <p className="esca-eyebrow text-accent" data-id="element-236" data-mp="36qkv8te562h1">
                      {c.cases.resultLabel}
                    </p>
                    <p className="mt-2.5 font-display text-lg font-semibold text-ink tracking-[-0.02em] leading-snug" data-id="element-237" data-mp="1swvykrlk6b7j">
                      {item.result}
                    </p>
                  </div>
                </div>

                <button type="button" onClick={() => toggle(i, item.role)} aria-expanded={isOpen} className="group flex items-center justify-between gap-3 w-full px-7 lg:px-8 py-5 border-t border-line text-left text-[0.9375rem] font-semibold text-ink transition-colors duration-200 hover:bg-sand" data-id="element-238" data-mp="1juedwhljxqmn">
                  {c.cases.cta}
                  <ArrowRightIcon className={`w-4 h-4 text-accent transition-transform duration-200 ease-out ${isOpen ? 'rotate-90' : 'group-hover:translate-x-1'}`} data-id="element-239" data-mp="115g3sa9uli68" />
                </button>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}